// WhatsApp phone normalization.
//
// `wa.me/<digits>` treats the path as an international number WITHOUT a '+', so
// it parses the leading digits as a country code. A bare Guatemala number that
// isn't given the 502 code gets misread (e.g. a 9-digit "584391133" reads as
// Venezuela "+58…") and WhatsApp reports "number does not exist". This helper
// returns a confidently-valid WhatsApp number, or '' when the value can't be
// normalized — callers then open wa.me WITHOUT a recipient so the agent picks
// the chat instead of messaging a wrong number.

// Known country codes (dial code w/o '+', expected TOTAL digit length incl. code)
// kept in sync with the PhoneInput country list (src/components/ui/PhoneInput.tsx).
const KNOWN_DIAL_CODES: ReadonlyArray<{ cc: string; total: number }> = [
  { cc: '502', total: 11 }, // Guatemala  (502 + 8)
  { cc: '503', total: 11 }, // El Salvador
  { cc: '504', total: 11 }, // Honduras
  { cc: '505', total: 11 }, // Nicaragua
  { cc: '506', total: 11 }, // Costa Rica
  { cc: '507', total: 11 }, // Panamá
  { cc: '501', total: 10 }, // Belice      (501 + 7)
  { cc: '52', total: 12 }, // México       (52 + 10)
  { cc: '57', total: 12 }, // Colombia     (57 + 10)
  { cc: '1', total: 11 }, // EE. UU./Canadá (1 + 10)
];

/**
 * Normalize a stored phone string into a WhatsApp-ready international number
 * (digits only, no '+'), or '' when it can't be confidently normalized.
 */
export function toWhatsAppNumber(raw?: string | null): string {
  let d = (raw || '').replace(/\D/g, '');
  if (d.startsWith('00')) d = d.slice(2); // drop intl access prefix: 00502… → 502…
  if (!d) return '';
  // Bare local Guatemala number (the dominant case): prepend the 502 code.
  if (d.length === 8) return `502${d}`;
  // Already carries a known country code at its expected total length.
  for (const { cc, total } of KNOWN_DIAL_CODES) {
    if (d.length === total && d.startsWith(cc)) return d;
  }
  // 9-digit, wrong length, or unknown code → not confidently valid.
  return '';
}

/**
 * Human-readable international format for phone numbers shown INSIDE share
 * message text: "50237952254" → "+502 3795-2254". WhatsApp's linkifier keeps
 * spaced/hyphenated international numbers tappable, so the friendlier form is
 * still a one-tap chat. Falls back to a bare "+digits" when the number isn't a
 * recognized shape, and '' when there's no number at all.
 */
export function formatPhoneDisplay(raw?: string | null): string {
  const normalized = toWhatsAppNumber(raw);
  if (!normalized) {
    const bare = (raw || '').replace(/\D/g, '');
    return bare ? `+${bare}` : '';
  }
  const entry = KNOWN_DIAL_CODES.find(({ cc, total }) => normalized.length === total && normalized.startsWith(cc));
  if (!entry) return `+${normalized}`;
  const national = normalized.slice(entry.cc.length);
  if (national.length === 8) return `+${entry.cc} ${national.slice(0, 4)}-${national.slice(4)}`;
  if (national.length === 10) return `+${entry.cc} ${national.slice(0, 3)} ${national.slice(3, 6)}-${national.slice(6)}`;
  return `+${entry.cc} ${national}`;
}

/**
 * Build a wa.me URL for a raw phone + message. Falls back to the no-recipient
 * picker (wa.me/?text=…) when the phone can't be confidently normalized.
 */
export function whatsAppLink(rawPhone: string | null | undefined, message: string): string {
  const num = toWhatsAppNumber(rawPhone);
  const text = encodeURIComponent(message);
  return num ? `https://wa.me/${num}?text=${text}` : `https://wa.me/?text=${text}`;
}

/**
 * Build a `tel:` href. Keeps a '+' only when the stored value already had one,
 * so a bare local number dials locally instead of being turned into a bogus
 * international one — call sites used to hardcode `tel:+${digits}`, which sent
 * a bare 8-digit Guatemala number out as +4217…, and a 9-digit one to Venezuela.
 * Deliberately permissive (unlike toWhatsAppNumber): a dialer shows the number
 * and the human can correct it, whereas WhatsApp just fails with "number does
 * not exist". Mirrors mobile's utils/links.ts openTel.
 */
export function telLink(rawPhone: string | null | undefined): string {
  return `tel:${(rawPhone || '').replace(/[^\d+]/g, '')}`;
}
